import React, { useEffect, useState } from 'react';
import Card from '../../ui/Card';
import Badge from '../../ui/Badge';

/**
 * Highlighted card showing the activity that's currently launched. Counter
 * ticks every second client-side, but the source of truth is always
 * `launchedAt` from the server (so there's no clock drift).
 *
 * The response progress denominator uses the *total* participant count
 * (everyone who ever joined, including those who left) — not just currently
 * active. Participants who responded then dropped off should still count
 * towards "responded", and the bar shouldn't artificially jump up when
 * someone leaves.
 */
const NowPlayingCard = ({ activity, totalCount }) => {
  const [now, setNow] = useState(Date.now());

  // Only run the per-second tick when the activity is actually timed —
  // a `timerEnabled` flag of false means the trainer chose to stop the
  // activity manually and there's no countdown to show.
  const hasTimer = activity?.timerEnabled === true && activity?.timeLimit && activity?.launchedAt;

  useEffect(() => {
    if (!hasTimer) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasTimer]);

  if (!activity) {
    return (
      <Card title="Now Playing" padding="md">
        <div className="py-8 text-center">
          <p className="text-sm text-neutral-500">No activity launched</p>
          <p className="text-xs text-neutral-400 mt-1">Waiting for trainer</p>
        </div>
      </Card>
    );
  }

  const responseCount = activity.responseCount || 0;
  const total = totalCount || 0;
  const pct = total > 0 ? Math.round((responseCount / total) * 100) : 0;

  let remainingLabel = null;
  if (hasTimer) {
    const launchedMs = new Date(activity.launchedAt).getTime();
    const elapsedMs = Math.max(0, now - launchedMs);
    const remainingSec = Math.max(0, Math.floor((activity.timeLimit * 1000 - elapsedMs) / 1000));
    const m = Math.floor(remainingSec / 60);
    const s = remainingSec % 60;
    remainingLabel = `${m}:${String(s).padStart(2, '0')}`;
  }

  return (
    <Card padding="md" className="border-2 border-emerald-200 bg-emerald-50/30">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Now Playing</p>
          <h3 className="text-base font-semibold text-neutral-900 mt-0.5">{activity.title || 'Activity'}</h3>
          {activity.subSessionName && (
            <p className="text-xs text-neutral-500 mt-0.5">{activity.subSessionName}</p>
          )}
        </div>
        <Badge variant="live" dot>{activity.type || 'live'}</Badge>
      </div>

      {remainingLabel && (
        <div className="mb-3">
          <p className="text-xs text-neutral-500">Remaining</p>
          <p className="text-2xl font-bold text-emerald-700 tabular-nums">{remainingLabel}</p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-neutral-700">Responses</span>
          <span className="text-xs font-semibold text-neutral-900">{responseCount}/{total} · {pct}%</span>
        </div>
        <div className="w-full bg-neutral-200 rounded-full h-1.5">
          <div className="h-1.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </Card>
  );
};

export default NowPlayingCard;
